import { addMarketplaceEntities } from '../../ducks/marketplaceData.duck';
import { SendgridApi, post } from '../../util/api';
import { IMAGE_FIELDS, IMAGE_VARIANTS } from '../../util/constants';
import { getAdminNotificationStatus, getUserFullName } from '../../util/dataExtractors';
import { dreamHomagePageNotificationData } from '../../util/dreamHomePageHelpers';
import { ListingCategory, StatusCode } from '../../util/enums';
import { storableError } from '../../util/errors';
import { showToaster } from '../../util/genericHelpers';
import { getImageVariantConfigs } from '../../util/helperFunctions';
import { types as sdkTypes } from '../../util/sdkLoader';
import { createSlug } from '../../util/urlHelpers';
const { UUID } = sdkTypes;

// ================ Action types ================ //

export const FETCH_USER_LISTING_REQUEST = 'app/DocumentManagementPage/FETCH_USER_LISTING_REQUEST';
export const FETCH_USER_LISTING_SUCCESS = 'app/DocumentManagementPage/FETCH_USER_LISTING_SUCCESS';
export const FETCH_USER_LISTING_ERROR = 'app/DocumentManagementPage/FETCH_USER_LISTING_ERROR';

export const UPDATE_LISTING_REQUEST = 'app/DocumentManagementPage/UPDATE_LISTING_REQUEST';
export const UPDATE_LISTING_SUCCESS = 'app/DocumentManagementPage/UPDATE_LISTING_SUCCESS';
export const UPDATE_LISTING_ERROR = 'app/DocumentManagementPage/UPDATE_LISTING_ERROR';

export const SEND_EMAIL_REQUEST = 'app/DocumentManagementPage/SEND_EMAIL_REQUEST';
export const SEND_EMAIL_SUCCESS = 'app/DocumentManagementPage/SEND_EMAIL_SUCCESS';
export const SEND_EMAIL_ERROR = 'app/DocumentManagementPage/SEND_EMAIL_ERROR';

export const SHOW_LISTING_REQUEST = 'app/DocumentManagementPage/SHOW_LISTING_REQUEST';
export const SHOW_LISTING_ERROR = 'app/DocumentManagementPage/SHOW_LISTING_ERROR';

// ================ Reducer ================ //

const initialState = {
  fetchUserListingInProgress: false,
  fetchUserListingError: null,
  userListing: null,
  updateListingInProgress: false,
  updateListingError: null,
  sendEmailInProgress: false,
  sendEmailError: null,
  id: null,
  showListingError: null,
};

export default function reducer(state = initialState, action = {}) {
  const { type, payload } = action;
  switch (type) {
    case FETCH_USER_LISTING_REQUEST:
      return {
        ...state,
        fetchUserListingInProgress: true,
        userListing: null,
        fetchUserListingError: null,
      };
    case FETCH_USER_LISTING_SUCCESS:
      return { ...state, fetchUserListingInProgress: false, userListing: payload };
    case FETCH_USER_LISTING_ERROR:
      console.error(payload); // eslint-disable-line no-console
      return {
        ...state,
        fetchUserListingInProgress: false,
        userListing: null,
        fetchUserListingError: payload,
      };
    case UPDATE_LISTING_REQUEST:
      return {
        ...state,
        updateListingInProgress: true,
        updateListingError: null,
      };
    case UPDATE_LISTING_SUCCESS:
      return { ...state, updateListingInProgress: false, updateListingError: null };
    case UPDATE_LISTING_ERROR:
      console.error(payload); // eslint-disable-line no-console
      return {
        ...state,
        updateListingInProgress: false,
        updateListingError: payload,
      };
    case SEND_EMAIL_REQUEST:
      return {
        ...state,
        sendEmailInProgress: true,
        sendEmailError: null,
      };
    case SEND_EMAIL_SUCCESS:
      return { ...state, sendEmailInProgress: false, sendEmailError: null };
    case SEND_EMAIL_ERROR:
      return {
        ...state,
        sendEmailInProgress: false,
        sendEmailError: payload,
      };

    case SHOW_LISTING_REQUEST:
      return { ...state, id: payload.id, showListingError: null };
    case SHOW_LISTING_ERROR:
      return { ...state, showListingError: payload };
    default:
      return state;
  }
}

// ================ Selectors ================ //
export const fetchUserListingInProgressSelector = state =>
  state.DocumentManagementPage.fetchUserListingInProgress;

export const sendEmailInProgressSelector = state =>
  state.DocumentManagementPage.sendEmailInProgress;

export const listingIdSelector = state => state.DocumentManagementPage.id;

export const userListingSelector = state => state.DocumentManagementPage.userListing;

// ================ Action creators ================ //

export const fetchUserListingRequest = () => ({ type: FETCH_USER_LISTING_REQUEST });

export const fetchUserListingSuccess = data => ({
  type: FETCH_USER_LISTING_SUCCESS,
  payload: data,
});

export const fetchUserListingError = e => ({
  type: FETCH_USER_LISTING_ERROR,
  error: true,
  payload: e,
});

export const updateListingRequest = () => ({ type: UPDATE_LISTING_REQUEST });

export const updateListingSuccess = () => ({
  type: UPDATE_LISTING_SUCCESS,
});

export const updateListingError = e => ({
  type: UPDATE_LISTING_ERROR,
  error: true,
  payload: e,
});
export const sendEmailRequest = () => ({ type: SEND_EMAIL_REQUEST });

export const sendEmailSuccess = () => ({
  type: SEND_EMAIL_SUCCESS,
});

export const sendEmailError = e => ({
  type: SEND_EMAIL_ERROR,
  error: true,
  payload: e,
});

export const showListingRequest = id => ({
  type: SHOW_LISTING_REQUEST,
  payload: { id },
});

export const showListingError = e => ({
  type: SHOW_LISTING_ERROR,
  error: true,
  payload: e,
});
export const fetchUserListingListing = () => async (dispatch, getState, sdk) => {
  dispatch(fetchUserListingRequest());

  const params = {
    page: 1,
    perPage: 1,
    pub_listingCategory: ListingCategory.DREAM_HOME,
  };

  try {
    const { data: { data = {} } = {} } = await sdk.ownListings.query(params);
    const listing = data[0];
    dispatch(fetchUserListingSuccess(listing));
    return listing;
  } catch (rawError) {
    const error = storableError(rawError);
    dispatch(fetchUserListingError(error));
    throw error; // so UI can catch it if needed
  }
};

export const deleteDocumentFromListing = (listing, fileKey) => async (dispatch, getState, sdk) => {
  dispatch(updateListingRequest());

  try {
    const publicData = listing?.attributes?.publicData || {};

    // Helper to drop any item whose `.key` matches fileKey
    const withoutKey = arr => (arr || []).filter(item => item.key !== fileKey);

    const planningPermissionMedia = withoutKey(publicData.planningPermissionMedia);
    const moodboardMediaFiles = withoutKey(publicData.moodboardMediaFiles);

    const updatedParams = {
      id: listing.id,
      publicData: {
        ...publicData,
        planningPermissionMedia,
        moodboardMediaFiles,
      },
    };

    // send the patch
    await sdk.ownListings.update(updatedParams);

    // optimistic success
    dispatch(updateListingSuccess());
  } catch (rawError) {
    const error = storableError(rawError);
    dispatch(updateListingError(error));
    throw error; // so UI can catch it if needed
  }
};

export const updateListing = params => async (dispatch, getState, sdk) => {
  dispatch(updateListingRequest());

  try {
    // send the patch
    await sdk.ownListings.update(params);
    // optimistic success
    dispatch(updateListingSuccess());
  } catch (rawError) {
    const error = storableError(rawError);
    dispatch(updateListingError(error));
    throw error; // so UI can catch it if needed
  }
};
export const sendEmail = (listing, config) => async (dispatch, getState, sdk) => {
  dispatch(sendEmailRequest());

  try {
    const state = getState();
    const currentUser = state.user.currentUser;
    const isAdminNotificationSent = getAdminNotificationStatus(listing);
    const listingTitle = listing.attributes?.title;
    const slug = createSlug(listingTitle);
    const completeListingLink = `${process.env.REACT_APP_MARKETPLACE_ROOT_URL}/admin-review/${slug}/${listing.id.uuid}`;
    const emailParams = dreamHomagePageNotificationData(completeListingLink);

    // Check if this listing came from an inquiry (has requestedListing in publicData)
    const requestedListing = listing.attributes?.publicData?.requestedListing;
    const hasInquiry = !!requestedListing;

    // Send admin notification email only if it has not been sent before.
    if (!isAdminNotificationSent) {
      // If this is an inquiry, send ONLY the inquiry notification email
      if (hasInquiry) {
        try {
          const userFullName = getUserFullName(currentUser);
          const customerEmail = currentUser?.attributes?.email;

          // Send inquiry notification email (server will also save to Firestore with admin permissions)
          await post('/api/send-admin-inquiry-notification', {
            customerEmail,
            customerName: userFullName || `${currentUser?.attributes?.profile?.firstName} ${currentUser?.attributes?.profile?.lastName}`,
            listingId: requestedListing,
            submissionDate: new Date().toString(),
            profileData: listing.attributes?.publicData, // Send profile data for Firestore save
          });

          // Mark that the admin notification has been sent.
          await sdk.ownListings.update(
            {
              id: listing.id.uuid,
              privateData: { isAdminNotificationSent: true },
            },
            { expand: true }
          );
          showToaster('success', 'Inquiry notification sent successfully');
        } catch (inquiryError) {
          console.error('Error sending inquiry notification:', inquiryError);
          showToaster('error', 'Failed to send inquiry notification');
          throw inquiryError;
        }
      } else {
        // Regular listing - send the standard listing completion email
        const emailResponse = await SendgridApi.sendGridNotification(emailParams);
        if (emailResponse.statusCode === StatusCode.SUCCESS) {
          // Mark that the admin notification has been sent.
          await sdk.ownListings.update(
            {
              id: listing.id.uuid,
              privateData: { isAdminNotificationSent: true },
            },
            { expand: true }
          );
          showToaster('success', 'Email sent successfully');
        }
      }
    }

    // optimistic success
    dispatch(sendEmailSuccess());
    dispatch(showListing(listing.id, config));
  } catch (rawError) {
    const error = storableError(rawError);
    dispatch(sendEmailError(error));
    showToaster('error', 'Email failed to send');
    throw error; // so UI can catch it if needed
  }
};

export const showListing = (listingId, config) => async (dispatch, getState, sdk) => {
  try {
    if (!listingId) {
      const error = new Error('Listing ID is required.');
      dispatch(showListingError(storableError(error)));
      return Promise.reject(error);
    }

    // Extract image configuration
    const {
      aspectWidth = 1,
      aspectHeight = 1,
      variantPrefix = IMAGE_VARIANTS.PREFIX,
    } = config.layout.listingImage;
    const aspectRatio = aspectHeight / aspectWidth;

    // Dispatch request action
    dispatch(showListingRequest(listingId));

    // Build API parameters
    const params = {
      id: listingId,
      include: ['author', 'author.profileImage', 'images', 'currentStock'],
      'fields.image': [
        ...IMAGE_FIELDS,
        `variants.${variantPrefix}`, // Cropped variants
        `variants.${variantPrefix}-2x`,
        `variants.${variantPrefix}-4x`,
        `variants.${variantPrefix}-6x`,
      ],
      ...getImageVariantConfigs(variantPrefix, aspectRatio),
    };

    // Make API call
    const data = await sdk.listings.show(params);

    // Process response
    const listingFields = config?.listing?.listingFields || [];
    const sanitizeConfig = { listingFields };
    dispatch(addMarketplaceEntities(data, sanitizeConfig));

    return data;
  } catch (error) {
    const errorToStore = storableError(error);
    dispatch(showListingError(errorToStore));
    throw errorToStore;
  }
};
// ================ Thunks ================ //

export const loadData = (params, search, config) => dispatch => {
  const queryParams = new URLSearchParams(search);
  const listingId = queryParams.get('listingId');

  if (listingId) {
    dispatch(showListing(new UUID(listingId), config));
  }
  return dispatch(fetchUserListingListing());
};
